/**
 * Presets System
 * Save and load visualizer configuration presets
 * Uses PresetService for hybrid local/cloud storage
 */

import { managers } from './state.js';
import { elements } from './dom-cache.js';
import { buildControlsUI, resetToDefaults } from './visualizer-controls.js';
import { presetService, PresetLimitError } from './preset-service.js';
import { authService } from '../auth/auth-service.js';
import { pricingModal } from '../payments/pricing-modal.js';

/**
 * Load presets (async wrapper for backward compatibility)
 * @deprecated Use presetService.getPresets() directly
 */
export async function getPresets() {
  return await presetService.getPresets();
}

/**
 * Save presets (sync fallback for migration)
 * @deprecated Use presetService.savePreset() directly
 */
export function savePresets(presets) {
  // Fallback to localStorage for any legacy code
  localStorage.setItem('audioVisualizerPresets', JSON.stringify(presets));
}

/**
 * Populate preset dropdown with saved presets
 */
export async function populatePresetSelect() {
  try {
    const presets = await presetService.getPresets();
    const options = Object.keys(presets).map(name =>
      `<option value="${name}">${name}</option>`
    ).join('');
    elements.presetSelect.innerHTML = '<option value="">-- Select Preset --</option>' + options;
  } catch (error) {
    console.error('[Presets] Error populating preset select:', error);
    elements.presetSelect.innerHTML = '<option value="">-- Select Preset --</option>';
  }
}

/**
 * Update sync status indicator in the UI
 */
function updateSyncStatusUI(status) {
  let indicator = document.getElementById('presetSyncStatus');

  // Create indicator if it doesn't exist
  if (!indicator) {
    const presetSection = document.querySelector('[data-section="Presets"] .accordion-content-inner');
    if (presetSection) {
      indicator = document.createElement('div');
      indicator.id = 'presetSyncStatus';
      indicator.className = 'preset-sync-status';
      presetSection.insertBefore(indicator, presetSection.firstChild);
    }
  }

  if (!indicator) return;

  // Only show status for authenticated non-anonymous users
  const shouldShowStatus = authService.isAuthenticated() && !authService.isAnonymous();

  if (!shouldShowStatus) {
    indicator.style.display = 'none';
    return;
  }

  indicator.style.display = 'flex';

  if (status.syncing) {
    indicator.innerHTML = '<span class="sync-icon spinning">&#x21bb;</span> Syncing...';
    indicator.className = 'preset-sync-status syncing';
  } else if (status.error) {
    indicator.innerHTML = '<span class="sync-icon">&#x26A0;</span> Offline';
    indicator.className = 'preset-sync-status error';
    indicator.title = status.error;
  } else if (status.lastSync) {
    const ago = formatTimeAgo(status.lastSync);
    indicator.innerHTML = `<span class="sync-icon">&#x2713;</span> Synced ${ago}`;
    indicator.className = 'preset-sync-status synced';
  }
}

/**
 * Format timestamp as relative time
 */
function formatTimeAgo(timestamp) {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);

  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

/**
 * Show toast notification
 */
function showToast(message, type = 'info') {
  // Use existing toast system if available, otherwise use alert
  const existingToast = document.querySelector('.toast-notification');
  if (existingToast) {
    existingToast.remove();
  }

  const toast = document.createElement('div');
  toast.className = `toast-notification toast-${type}`;
  toast.textContent = message;
  document.body.appendChild(toast);

  // Auto-remove after 3 seconds
  setTimeout(() => toast.remove(), 3000);
}

/**
 * Show upgrade prompt for preset limit
 */
function showUpgradePrompt(options) {
  // Use pricing modal if available
  if (pricingModal) {
    pricingModal.show('preset_limit');
    return;
  }

  // Fallback to simple alert
  alert(`${options.title}\n\n${options.message}`);
}

/**
 * Set up preset button event listeners
 */
export function setupPresetListeners() {
  // Subscribe to preset service changes
  presetService.onPresetsChange((status) => {
    updateSyncStatusUI(status);
  });

  // Save current settings as preset
  document.getElementById('savePresetBtn')?.addEventListener('click', async () => {
    const name = elements.presetNameInput.value.trim();
    if (!name) {
      showToast('Please enter a preset name', 'error');
      return;
    }

    try {
      await presetService.savePreset(name, {
        visualizationType: managers.visualizerManager.getType(),
        config: managers.visualizerManager.getConfig()
      });

      await populatePresetSelect();
      elements.presetSelect.value = name;
      elements.presetNameInput.value = '';
      showToast(`Preset "${name}" saved!`, 'success');
    } catch (error) {
      if (error instanceof PresetLimitError) {
        showUpgradePrompt({
          title: 'Preset Limit Reached',
          message: error.message
        });
      } else {
        console.error('[Presets] Save error:', error);
        showToast(`Failed to save preset: ${error.message}`, 'error');
      }
    }
  });

  // Load selected preset
  document.getElementById('loadPresetBtn')?.addEventListener('click', async () => {
    const name = elements.presetSelect.value;
    if (!name) {
      showToast('Please select a preset to load', 'error');
      return;
    }

    try {
      const presets = await presetService.getPresets();
      const preset = presets[name];
      if (!preset) return;

      // Set visualization type
      if (preset.visualizationType) {
        if (elements.vizTypeSelect) {
          elements.vizTypeSelect.value = preset.visualizationType;
        }
        await managers.visualizerManager.setType(preset.visualizationType);
      }

      // Apply config
      if (preset.config) {
        managers.visualizerManager.setConfig(preset.config);
      }

      // Rebuild UI to reflect new values
      buildControlsUI();
      managers.visualizerManager.clear();

      showToast(`Preset "${name}" loaded`, 'success');
    } catch (error) {
      console.error('[Presets] Load error:', error);
      showToast(`Failed to load preset: ${error.message}`, 'error');
    }
  });

  // Delete selected preset
  document.getElementById('deletePresetBtn')?.addEventListener('click', async () => {
    const name = elements.presetSelect.value;
    if (!name) {
      showToast('Please select a preset to delete', 'error');
      return;
    }

    if (!confirm(`Delete preset "${name}"?`)) return;

    try {
      await presetService.deletePreset(name);
      await populatePresetSelect();
      showToast(`Preset "${name}" deleted`, 'success');
    } catch (error) {
      console.error('[Presets] Delete error:', error);
      showToast(`Failed to delete preset: ${error.message}`, 'error');
    }
  });

  // Reset to defaults
  document.getElementById('resetVisualizerSettingsBtn')?.addEventListener('click', () => {
    resetToDefaults();
  });
}
